import { useEffect, useState } from "react";
import { api } from "./api.js";
import { useLocale } from "./ui.js";

interface DesktopReleaseInfo {
  version: string;
  installerUrl: string;
  installerSha256?: string | null;
  releaseNotesUrl?: string | null;
  signatureVerified?: boolean;
  signer?: string;
}

export function DesktopDownloadCta(): React.JSX.Element {
  const { tr } = useLocale();
  const [info, setInfo] = useState<DesktopReleaseInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [errored, setErrored] = useState(false);

  useEffect(() => {
    let mounted = true;
    api
      .publicDesktopRelease()
      .then((res) => {
        if (!mounted) return;
        // Public policy: only a signature-VERIFIED release renders a
        // download — an unsigned/beta artifact never gets a public button.
        if (res.ok && res.data?.installerUrl && res.data.signatureVerified === true) {
          setInfo(res.data);
        } else {
          setErrored(true);
        }
      })
      .catch(() => setErrored(true))
      .finally(() => setLoading(false));
    return () => { mounted = false; };
  }, []);

  if (loading) {
    return (
      <div className="ss-desktop-cta">
        <span className="ss-pill gray">{tr("loading")}</span>
      </div>
    );
  }

  if (info) {
    return (
      <div className="ss-desktop-cta">
        <a className="ss-btn-primary" href={info.installerUrl} download>
          {tr("desktopDownloadLabel")} {info.version}
        </a>
        <span className="ss-pill gray">
          {tr("desktopDownloadSignedHelp")}
          {info.signer ? ` · ${info.signer}` : ""}
          {info.installerSha256 ? ` · SHA256 ${info.installerSha256.slice(0, 12)}…` : ""}
        </span>
        {info.releaseNotesUrl ? (
          <a className="ss-link" href={info.releaseNotesUrl} target="_blank" rel="noreferrer">
            {tr("desktopDownload")}
          </a>
        ) : null}
      </div>
    );
  }

  return (
    <div className="ss-desktop-cta" role="status" aria-live="polite">
      <strong>{tr("desktopDownloadBlockedTitle")}</strong>
      <span className="ss-pill gray">
        {errored ? tr("desktopDownloadUnavailable") : tr("desktopDownloadSoonHelp")}
      </span>
    </div>
  );
}
